
import {
	createContext, useContext, useEffect, useState
} from 'react'

import type { Socket } from 'socket.io-client'
import { useImmer } from 'use-immer'

import { ApiCaller } from '@frontend/api'
import type {
	Application, Block, Brain, Chat, Company, Notification, Settings, User, UserMe
} from '@types'
import { useSentry } from '@utils/frontend'

const NOT_FOUND = -1

interface IAppContext {
	// Values
	readonly user: User;
	readonly company?: Company | undefined;
	readonly brains: Brain[];
	readonly chats: Chat[];
	readonly applications: Application[];
	readonly settings: Settings;
	readonly notifications: Notification[];
	readonly newNotification: boolean;

	// Chat methods
	readonly addChat: (chat: Chat) => void;
	readonly updateChat: (chat: Partial<Chat> & {id: string}) => void;
	readonly removeChat: (id: string) => void;

	// Notifications methods
	readonly addNotification: (notification: Notification, displayNotification?: boolean | undefined) => void;
	readonly updateNotification: (notification: Notification) => void;
	readonly removeNotification: (id: string) => void;
	readonly setNotifications: (notifications: Notification[]) => void;
	readonly setNewNotification: (newNotification: boolean) => void;
	readonly clearNotifications: () => void;

	// Brain methods
	readonly addBrain: (brain: Brain) => void;
	readonly updateBrain: (brain: Partial<Brain> & {readonly id: string}) => void;
	readonly removeBrain: (id: string) => void;

	// Application methods
	readonly addApplication: (application: Application) => void;
	readonly updateApplication: (application: Partial<Application> & {readonly id: string}) => void;
	readonly removeApplication: (id: string) => void;

	// User methods
	readonly updateUser: (user: User) => void;

	// Settings methods
	readonly updateSettings: (settings: Settings) => void;

	// Booleans
	readonly hasCompany: boolean;

	// Collapse state
	readonly isSidebarCollapsed: boolean;
	readonly setIsSidebarCollapsed: (collapsed: boolean) => void;

	// Active brain state
	readonly activeBrain?: Brain | undefined;
	readonly setActiveBrain: (activeBrain: Brain | undefined) => void;

	// Application blocks state
	readonly activeBlock?: Block | undefined;
	readonly setActiveBlock: (activeBlock: Block | undefined) => void;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const AppContext = createContext<IAppContext | undefined>(undefined)

export default function AppProvider({ children, me, socket, initialChats, initialBrains, initialApplications }:
	{readonly children: React.ReactNode; readonly me: UserMe; readonly socket: Socket; readonly initialChats: Chat[]; readonly initialBrains: Brain[]; readonly initialApplications: Application[]}) {
	const [user, setUser] = useState<User>({
		id: me.id,
		email: me.email,
		name: me.name
	})
	const [chats, updateChats] = useImmer(initialChats)
	const [brains, updateBrains] = useImmer(initialBrains)
	const [applications, updateApplications] = useImmer(initialApplications)
	const [settings, setSettings] = useState(me.settings)
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 768)
	const [activeBrain, setActiveBrain] = useState<Brain | undefined>(undefined)
	const [activeBlock, setActiveBlock] = useState<Block | undefined>(undefined)
	const [notifications, updateNotifications] = useImmer<Notification[]>(me.notifications)
	const [newNotification, setNewNotification] = useState(false)
	const { captureException } = useSentry()

	// Notification Socket listeners
	useEffect(() => {
		function addOrUpdateNotification({ notification }: {notification: Notification}) {
			updateNotifications(draft => {
				const index = draft.findIndex(n => n.id === notification.id)

				if (index === NOT_FOUND) {
					setNewNotification(true)
					draft.unshift(notification)
				} else {
					draft[index] = notification
				}
			})
		}

		socket.on('notification:updated', addOrUpdateNotification)
		socket.on('notification:added', addOrUpdateNotification)

		return () => {
			socket.off('notification:updated', addOrUpdateNotification)
			socket.off('notification:added', addOrUpdateNotification)
		}
	}, [])

	useEffect(() => {
		async function loadEmptyBrains() {
			try {
				const emptyBrains = await ApiCaller.getEmptyBrains(brains.map(brain => brain.id))

				updateBrains(draft => {
					draft.forEach((brain, index) => {
						draft[index] = {
							...brain,
							isEmpty: !emptyBrains[index]
						}
					})
				})
			} catch (error) {
				captureException(error)
			}
		}

		loadEmptyBrains()
	}, [])

	useEffect(() => {
		const localIsSidebarCollapsed = localStorage.getItem('isSidebarCollapsed')

		setIsSidebarCollapsed(localIsSidebarCollapsed === 'true')
	}, [])

	// -----
	// Chats methods
	// -----
	function addChat(chat: Chat) {
		updateChats(draft => {
			draft.unshift(chat)
		})
	}

	function updateChat(chat: Partial<Chat> & {id: string}) {
		updateChats(draft => {
			const index = draft.findIndex(c => c.id === chat.id)

			if (index !== NOT_FOUND) {
				draft[index] = { ...draft[index], ...chat }
			}
		})
	}

	function removeChat(id: string) {
		updateChats(draft => {
			const index = draft.findIndex(c => c.id === id)

			if (index !== NOT_FOUND) {
				draft.splice(index, 1)
			}
		})
		const lastChat = sessionStorage.getItem('lastChat')

		if (lastChat === id) {
			sessionStorage.removeItem('lastChat')
		}
	}

	// -----
	// Notifications methods
	// -----
	function addNotification(notification: Notification, displayNotification = true) {
		setNewNotification(displayNotification)
		updateNotifications(draft => {
			draft.unshift(notification)
		})
	}

	function updateNotification(notification: Notification) {
		updateNotifications(draft => {
			const index = draft.findIndex(c => c.id === notification.id)

			if (index !== NOT_FOUND) {
				draft[index] = notification
			}
		})
	}

	function removeNotification(id: string) {
		updateNotifications(draft => {
			const index = draft.findIndex(c => c.id === id)

			if (index !== NOT_FOUND) {
				draft.splice(index, 1)
			}
		})
	}

	function clearNotifications() {
		updateNotifications(() => [])
	}

	// -----
	// Brains methods
	// -----
	function addBrain(brain: Brain) {
		updateBrains(draft => {
			draft.push(brain)
		})
	}

	function updateBrain(brain: Partial<Brain> & {readonly id: string}) {
		updateBrains(draft => {
			const index = draft.findIndex(b => b.id === brain.id)

			if (index !== NOT_FOUND) {
				draft[index] = {
					...draft[index],
					...brain
				}
			}
		})
	}

	function removeBrain(id: string) {
		updateBrains(draft => {
			const index = draft.findIndex(b => b.id === id)

			if (index !== NOT_FOUND) {
				draft.splice(index, 1)
			}
		})
	}

	// -----
	// Application methods
	// -----
	function addApplication(application: Application) {
		updateApplications(draft => {
			draft.push(application)
		})
	}

	function updateApplication(application: Partial<Application> & {readonly id: string}) {
		updateApplications(draft => {
			const index = draft.findIndex(a => a.id === application.id)

			if (index !== NOT_FOUND) {
				draft[index] = {
					...draft[index],
					...application
				}
			}
		})
	}

	function removeApplication(id: string) {
		updateApplications(draft => {
			const index = draft.findIndex(a => a.id === id)

			if (index !== NOT_FOUND) {
				draft.splice(index, 1)
			}
		})
	}

	// -----
	// User methods
	// -----
	function updateUser(user: User) {
		setUser(user)
	}

	// -----
	// Settings methods
	// -----
	function updateSettings(settings: Settings) {
		setSettings(settings)
	}

	return (
		<AppContext.Provider
			value={ {
				user,
				company: me.company,
				brains: [...brains].sort((a, b) => a.name.localeCompare(b.name)),
				chats,
				applications,
				settings,
				notifications,
				newNotification,

				addChat,
				updateChat,
				removeChat,

				addNotification,
				updateNotification,
				removeNotification,
				clearNotifications,
				setNotifications: updateNotifications,
				setNewNotification,

				addBrain,
				updateBrain,
				removeBrain,

				addApplication,
				updateApplication,
				removeApplication,

				updateUser,
				updateSettings,

				hasCompany: Boolean(me.company),

				isSidebarCollapsed,
				setIsSidebarCollapsed,

				activeBrain,
				setActiveBrain,

				activeBlock,
				setActiveBlock
			} }
		>
			{ children }
		</AppContext.Provider>
	)
}

export function useAppContext() {
	const context = useContext(AppContext)

	if (!context) {
		throw new Error('useAppContext must be used within a AppProvider')
	}

	return context
}
